import React, { useState } from 'react';
import { Bell, BellRing, X, AlertTriangle, FileText, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

// Componente de demonstração de notificações push
export const NotificationDemo = ({ onClose }) => {
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState('documento');

  const notifications = {
    documento: {
      title: '📄 Documento Vencendo',
      body: 'Alvará Sanitário vence em 7 dias! Clique para renovar.',
      icon: FileText,
      color: 'text-amber-500',
      bgColor: 'bg-amber-50'
    },
    verificacao: {
      title: '⏰ Verificação Pendente',
      body: 'Termômetro da Geladeira não foi verificado às 08:00.',
      icon: Clock,
      color: 'text-red-500',
      bgColor: 'bg-red-50'
    },
    trial: {
      title: '⚠️ Trial Expirando',
      body: 'Seu período de teste expira em 2 dias. Assine agora!',
      icon: AlertTriangle,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50'
    },
    ticket: {
      title: '✅ Ticket Respondido',
      body: 'Seu ticket #123 foi respondido pelo suporte.',
      icon: CheckCircle2,
      color: 'text-green-500',
      bgColor: 'bg-green-50'
    }
  };

  const currentNotif = notifications[notificationType];
  const IconComponent = currentNotif.icon;

  const simulateNotification = (type) => {
    setNotificationType(type);
    setShowNotification(true);
    
    // Auto-hide após 5 segundos
    setTimeout(() => setShowNotification(false), 5000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <BellRing className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <CardTitle className="text-xl">Preview: Notificações Push</CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Veja como as notificações apareceriam no seu navegador
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Área de simulação */}
          <div className="relative bg-slate-100 dark:bg-slate-800 rounded-xl p-6 min-h-[200px]">
            {/* Ícone de sino pulsando */}
            <div className="absolute top-4 right-4 animate-pulse">
              <Bell className="w-6 h-6 text-slate-400" />
            </div>
            
            {/* Notificação simulada */}
            {showNotification && (
              <div className="absolute top-4 right-4 w-80 animate-slide-in">
                <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                  {/* Header do navegador */}
                  <div className="bg-slate-50 dark:bg-slate-800 px-3 py-2 flex items-center gap-2 border-b">
                    <img 
                      src="/favicon.ico" 
                      alt="icon" 
                      className="w-4 h-4"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                      Conforme a Resolução
                    </span>
                    <span className="text-xs text-slate-400 ml-auto">agora</span>
                  </div>
                  
                  {/* Conteúdo */}
                  <div className="p-4 flex gap-3">
                    <div className={`p-2 rounded-lg ${currentNotif.bgColor}`}>
                      <IconComponent className={`w-5 h-5 ${currentNotif.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-900 dark:text-white">
                        {currentNotif.title}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        {currentNotif.body}
                      </p>
                    </div>
                    <button 
                      onClick={() => setShowNotification(false)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {!showNotification && (
              <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                <Bell className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">Clique em um botão abaixo para simular</p>
              </div>
            )}
          </div>

          {/* Botões de teste */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button 
              variant="outline" 
              className="flex flex-col h-auto py-3 gap-1"
              onClick={() => simulateNotification('documento')}
            >
              <FileText className="w-5 h-5 text-amber-500" />
              <span className="text-xs">Documento</span>
            </Button>
            <Button 
              variant="outline" 
              className="flex flex-col h-auto py-3 gap-1"
              onClick={() => simulateNotification('verificacao')}
            >
              <Clock className="w-5 h-5 text-red-500" />
              <span className="text-xs">Verificação</span>
            </Button>
            <Button 
              variant="outline" 
              className="flex flex-col h-auto py-3 gap-1"
              onClick={() => simulateNotification('trial')}
            >
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <span className="text-xs">Trial</span>
            </Button>
            <Button 
              variant="outline" 
              className="flex flex-col h-auto py-3 gap-1"
              onClick={() => simulateNotification('ticket')}
            >
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span className="text-xs">Ticket</span>
            </Button>
          </div>

          {/* Informações */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">
              📱 Como funcionaria:
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
              <li>• Notificações aparecem mesmo com o navegador minimizado</li>
              <li>• Usuário escolhe quais tipos de alerta quer receber</li>
              <li>• Clique na notificação leva direto para a ação necessária</li>
              <li>• Funciona em Desktop e Mobile (Chrome, Firefox, Edge)</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationDemo;
